import { yupResolver } from '@hookform/resolvers/yup'
import type { RestRecord, RestType } from '@libs/data-access/entities'
import { useCommonAppTranslation } from '@libs/utils/translations'
import RestTypes, {
  useRestName,
  DEFAULT_REST,
  REST_ACCIDENT_ID
} from '@spa/constants/RestTypes'
import type { DoubleStringTuple } from '@spa/features/Scheduling'
import type { ReduxState } from '@spa/redux/ReduxState'
import { membershipsSelectors } from '@spa/scenes/Plannings/redux/selectors'
import { keys } from 'lodash-es'
import type { Moment } from 'moment'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import type { DayFieldsValue } from '../RestFormSection/components/DayFields/DayFields'
import { getDefaultDateRange } from '../utils/getDefaultDateRange'
import { getDefaultTimeRange } from '../utils/getDefaultTimeRange'
import { getMomentDates } from '../utils/getMomentDates'
import { useRestFormValidations } from '../validation/restFormValidations'

type TParams = {
  rest: RestRecord
}

export type RestFormData = {
  hasError?: boolean
  contracts_ids_shift_overlap: string[]
  shift_conflict_strategy?: 'delete' | 'unassign' | 'none'
  note?: string
  rest_type: {
    label: string
    value: RestType
  }
  dateRange: DayFieldsValue
  time_range: DoubleStringTuple
  user_contract_ids: {
    label: string
    value: any
  }[]
  momentStart: Moment
  momentEnd: Moment
  accident_date?: string
}

export const useRestRHF = ({ rest }: TParams) => {
  const { t } = useCommonAppTranslation()
  const restTypeLabel = useRestName(rest?.rest_type)
  const restFormValidations = useRestFormValidations()
  const defaultStart = moment(rest.starts_at)
  const defaultEnd = moment(rest.ends_at)
  const defaultContractId = rest.user_contract_id
  const defaultContract = useSelector((state: ReduxState) =>
    state.contracts.getIn(['data', defaultContractId])
  )
  const defaultMembership = membershipsSelectors.useGetCachedById(
    defaultContract?.membership_id
  )
  const defaultSelectValue = defaultContractId
    ? [
        {
          value: String(rest.user_contract_id),
          label: t('models.fullname', {
            value: {
              firstname: defaultMembership?.firstname,
              lastname: defaultMembership?.lastname
            }
          })
        }
      ]
    : []

  const defaultValues: RestFormData = {
    rest_type: {
      value: rest.rest_type || '',
      label: restTypeLabel || ''
    },
    note: rest.note || '',
    user_contract_ids: defaultSelectValue,
    dateRange: getDefaultDateRange({ defaultStart, defaultEnd, rest }),
    time_range: getDefaultTimeRange({
      defaultStart,
      defaultEnd,
      rest
    }),
    contracts_ids_shift_overlap: [],
    accident_date:
      rest.accident_date ||
      getDefaultDateRange({ defaultStart, defaultEnd, rest }).start.date,
    momentStart: moment(rest.starts_at) || moment(),
    momentEnd: moment(rest.ends_at) || moment()
  }

  const methods = useForm({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(restFormValidations)
  })
  const {
    formState: { errors },
    watch
  } = methods

  const restTypeFormValue = watch('rest_type')?.value

  const selectedRest =
    restTypeFormValue && RestTypes.get(String(restTypeFormValue), DEFAULT_REST)
  const selectedRestType = selectedRest?.type

  const timeRange = watch('time_range')
  const dateRange = watch('dateRange')
  const { momentStart, momentEnd } = getMomentDates({
    defaultStart,
    selectedRestType,
    timeRange,
    dateRange
  })

  const formData: RestFormData = {
    hasError: !!keys(errors).length,
    contracts_ids_shift_overlap: watch('contracts_ids_shift_overlap'),
    shift_conflict_strategy: watch('shift_conflict_strategy'),
    dateRange,
    rest_type: watch('rest_type'),
    time_range: timeRange,
    user_contract_ids: watch('user_contract_ids'),
    momentStart,
    momentEnd,
    accident_date:
      selectedRest?.id !== REST_ACCIDENT_ID ? undefined : watch('accident_date')
  }

  return {
    methods,
    formData
  }
}
